import { useQueries } from '@tanstack/react-query';
import { CircleAlert, HardDrive, Pause, VideoOff } from 'lucide-react';
import { useMemo } from 'react';

import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { LivePlayer, type VideoSource } from '../live/LivePlayer';
import {
  searchDeviceRecordings,
  searchErrorLabels,
  startDeviceHLS,
  startDeviceWebRTC,
} from '../playback/deviceApi';
import { coverage } from '../playback/ranges';
import { deviceStart, windowEnd, windowsAt } from './sync';

interface Props {
  deviceId: string;
  name: string;
  /** The shared clock: recording instant to show now. */
  t: number;
  /** Last discontinuity of the clock (start, seek or resume). */
  epoch: number;
  playing: boolean;
}

/**
 * One camera of the mosaic window replaying the recording kept on the device
 * itself (DVR/NVR, ADR-020/022/031). The device streams forward at normal
 * speed from a moment, so a session opens where the clock was set (or where it
 * enters a recorded span) and runs on its own; pausing closes it.
 */
export function DeviceSyncTile({ deviceId, name, t, epoch, playing }: Props) {
  const { request } = useAuth();
  const windows = windowsAt(t);
  const searches = useQueries({
    queries: windows.map((w) => ({
      queryKey: ['wall-device-recordings', deviceId, w],
      queryFn: () => searchDeviceRecordings(request, deviceId, w, windowEnd(w)),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const spans = coverage(
    searches
      .flatMap((q) => q.data?.spans ?? [])
      .map((s) => ({ id: '', start: Date.parse(s.from), end: Date.parse(s.to) }))
      .filter((s) => s.end > s.start)
      .sort((a, b) => a.start - b.start),
  );
  // No search (web port closed) but the device replays from any time over
  // RTSP: open the stream at the clock's last jump (ADR-041).
  const direct = searches.some((q) => q.data?.direct_replay === true);
  const blind = direct && searches.some((q) => q.data && !q.data.ok);
  const startAt = !playing
    ? null
    : blind
      ? Math.floor(epoch / 1000) * 1000
      : deviceStart(spans, t, epoch);

  const source: VideoSource | undefined = useMemo(
    () =>
      startAt === null
        ? undefined
        : {
            key: String(startAt),
            badge: 'Gravação no equipamento',
            webrtc: (sdp, signal) => startDeviceWebRTC(request, deviceId, startAt, sdp, signal),
            hls: (signal) => startDeviceHLS(request, deviceId, startAt, signal),
          },
    [request, deviceId, startAt],
  );

  const failed = searches.find((q) => q.isError);
  if (failed) {
    const unsupported = failed.error instanceof ApiError && failed.error.status === 409;
    return (
      <Notice
        icon={unsupported ? HardDrive : CircleAlert}
        text={
          unsupported
            ? 'Este equipamento não oferece as gravações dele'
            : 'Não foi possível buscar no equipamento'
        }
      />
    );
  }
  const refused = searches.find((q) => q.data && !q.data.ok)?.data;
  if (refused && !blind) {
    return (
      <Notice
        icon={CircleAlert}
        text={searchErrorLabels[refused.error_code ?? ''] ?? 'O equipamento recusou a busca.'}
      />
    );
  }
  if (searches.some((q) => q.isPending)) {
    return (
      <div role="status" className="grid size-full place-items-center bg-canvas text-center">
        <p className="text-2xs text-fg-muted">Buscando no equipamento…</p>
      </div>
    );
  }
  if (!playing) return <Notice icon={Pause} text="Pausado" />;
  if (!source) return <Notice icon={VideoOff} text="Sem gravação neste momento" />;
  return (
    <LivePlayer
      deviceId={deviceId}
      profileToken=""
      source={source}
      label={`Gravação no equipamento: ${name}`}
      variant="tile"
    />
  );
}

function Notice({ icon: Icon, text }: { icon: typeof VideoOff; text: string }) {
  return (
    <div className="grid size-full place-items-center bg-canvas px-3 text-center">
      <div className="flex flex-col items-center gap-1.5">
        <Icon aria-hidden="true" className="size-4 text-fg-subtle" strokeWidth={1.75} />
        <p className="text-2xs text-fg-muted">{text}</p>
      </div>
    </div>
  );
}
